import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { toast } from "react-hot-toast";
import WizardStepsIndicator from "./WizardStepsIndicator";
import RoleListView from "./RoleListView";
import FetchAccessView from "./FetchAccessView";
import ScreenAccessView from "./ScreenAccessView";
import DataAccessView from "./DataAccessView";
import RoleFormPopup from "./RoleFormPopup";
import useRoleCrud, { DeleteRoleConfirmModal } from "./useRoleCrud";
import useAccessCrud from "./useAccessCrud";
import { getScreenCatalogForRole } from "../../../rbac/catalog";
import api from "../../../services/api";
import { usePermissions } from "../../../rbac/PermissionsContext";

const sanitizeScreenAccessForSubmit = (access) => {
  const source = access && typeof access === "object" ? access : {};
  const cleaned = {};

  for (const [moduleKey, moduleValue] of Object.entries(source)) {
    if (!moduleValue || typeof moduleValue !== "object") continue;
    const screens = {};
    if (moduleValue.screens && typeof moduleValue.screens === "object") {
      for (const [screenKey, screenValue] of Object.entries(moduleValue.screens)) {
        if (!screenValue || typeof screenValue !== "object") continue;
        const screenPerms = {};
        for (const action of ["view", "create", "edit", "delete", "approve", "export"]) {
          if (screenValue[action] === true) screenPerms[action] = true;
        }
        if (Object.keys(screenPerms).length > 0) {
          screens[screenKey] = screenPerms;
        }
      }
    }
    const moduleObj = { screens };
    const hasView = Object.values(screens).some((scr) => !!scr.view);
    if (hasView || moduleValue.view === true) {
      moduleObj.view = true;
    }
    cleaned[moduleKey] = moduleObj;
  }

  return cleaned;
};

/**
 * Owns every piece of user-role-management state and renders the wizard.
 * CRUD logic lives in two companion hooks (`useRoleCrud`, `useAccessCrud`)
 * so this file stays focused on wiring + layout.
 *
 * Parent (GlobalSettingsModal) passes:
 *  - currentUserRole          : role-gating
 *  - roles, fetchRoles        : role list (shared with WorkflowManagement)
 *  - formData/setFormData     : shared form state (roleName/description live here)
 *  - handleInputChange        : shared input handler
 *  - filterResults/setShowFilters : shared Filters modal plumbing
 *  - branches/departments     : shared master data for Data Access picker
 */
export default function UserRoleManagement({
  currentUserRole,
  roles,
  fetchRoles,
  formData,
  setFormData,
  handleInputChange,
  filterResults,
  setFilterResults,
  setShowFilters,
  branches,
  departments,
}) {
  const { hasModuleAccess, hasScreenAccess, isAdmin } = usePermissions();
  // -- Role-specific state (encapsulated here) ----------------------------
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [screenAccess, setScreenAccess] = useState({});
  const [dataAccess, setDataAccess] = useState({});
  const [selectedRoleForAccess, setSelectedRoleForAccess] = useState(null);
  const [searchRoles, setSearchRoles] = useState("");
  const [showSystemRoles, setShowSystemRoles] = useState(false);
  const [openRoleMenuId, setOpenRoleMenuId] = useState(null);
  const roleMenuRef = useRef(null);
  const [roleAssignmentType, setRoleAssignmentType] = useState(null);
  const [fetchFromRoleId, setFetchFromRoleId] = useState(null);
  const [selectedModuleForAccess, setSelectedModuleForAccess] = useState(null);

  // Wizard state (stage-by-stage role creation flow)
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardRoleType, setWizardRoleType] = useState(null);
  const [wizardRoleId, setWizardRoleId] = useState(null);
  const [pendingRoleDraft, setPendingRoleDraft] = useState(null);
  const [shouldFetchAccess, setShouldFetchAccess] = useState(null);

  // Admin and super_admin can both manage roles. Backend routes already accept
  // "admin" for POST/PUT/DELETE on /roles, so aligning the UI avoids the
  // previous situation where the tab was empty for admin accounts.
  const canViewUserRoles =
    isAdmin ||
    hasModuleAccess("user_roles", "view") ||
    hasScreenAccess("user_roles", "role_list", "view") ||
    hasScreenAccess("user_roles", "screen_access", "view") ||
    hasScreenAccess("user_roles", "data_access", "view");
  const canCreateRole =
    isAdmin ||
    hasModuleAccess("user_roles", "create") ||
    hasScreenAccess("user_roles", "role_list", "create");
  const canEditRole =
    isAdmin ||
    hasModuleAccess("user_roles", "edit") ||
    hasScreenAccess("user_roles", "role_list", "edit");
  const canDeleteRole =
    isAdmin ||
    hasModuleAccess("user_roles", "delete") ||
    hasScreenAccess("user_roles", "role_list", "delete");
  const canEditScreenAccess =
    isAdmin ||
    hasModuleAccess("user_roles", "edit") ||
    hasScreenAccess("user_roles", "screen_access", "edit");
  const canEditDataAccess =
    isAdmin ||
    hasModuleAccess("user_roles", "edit") ||
    hasScreenAccess("user_roles", "data_access", "edit");
  const isSuperAdmin = canViewUserRoles;

  const roleForAccessWizard = useMemo(() => {
    if (editingRole) return editingRole;
    if (wizardRoleId != null && !String(wizardRoleId).startsWith("draft_")) {
      return roles.find((r) => String(r.id) === String(wizardRoleId)) || null;
    }
    return null;
  }, [editingRole, wizardRoleId, roles]);

  const accessCatalog = useMemo(
    () => getScreenCatalogForRole(roleForAccessWizard),
    [roleForAccessWizard]
  );

  // -- CRUD handlers via hooks -------------------------------------------
  const {
    resetRoleForm,
    resetWizard,
    handleEditRole,
    handleDeactivateRole,
    handleReactivateRole,
    handleDeleteRole,
    confirmDeleteRole,
    cancelDeleteRole,
    deleteConfirmRoleId,
    deleteRoleInProgress,
    handleRoleSubmit,
  } = useRoleCrud({
    roles,
    fetchRoles,
    formData,
    setFormData,
    filterResults,
    setFilterResults,
    editingRole,
    setEditingRole,
    roleAssignmentType,
    setRoleAssignmentType,
    wizardRoleType,
    setWizardRoleType,
    setWizardRoleId,
    setWizardStep,
    setShouldFetchAccess,
    setFetchFromRoleId,
    setSelectedRoleForAccess,
    setSelectedModuleForAccess,
    setShowRoleForm,
    setPendingRoleDraft,
    canCreateRole,
    canEditRole,
    canDeleteRole,
    canEditScreenAccess,
    canEditDataAccess,
  });

  const {
    handleSelectAllModule,
    handleScreenPermissionChange,
    handleDataAccessChange,
    handleDataAccessItemToggle,
  } = useAccessCrud({
    isSuperAdmin,
    screenAccess,
    setScreenAccess,
    dataAccess,
    setDataAccess,
    selectedRoleForAccess,
    canEditScreenAccess,
    canEditDataAccess,
    roles,
    accessCatalog,
  });

  // -- Side effects only this component cares about -----------------------

  // Auto-select first module when entering screen-access configuration.
  // selectedModuleForAccess now stores the canonical moduleKey (not label).
  useEffect(() => {
    if (!(selectedRoleForAccess || wizardRoleId)) return;
    if (accessCatalog.length === 0) return;
    const keys = new Set(accessCatalog.map((m) => m.key));
    if (!selectedModuleForAccess || !keys.has(selectedModuleForAccess)) {
      setSelectedModuleForAccess(accessCatalog[0].key);
    }
  }, [selectedRoleForAccess, wizardRoleId, accessCatalog, selectedModuleForAccess]);

  // Close role menu when clicking outside.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (roleMenuRef.current && !roleMenuRef.current.contains(event.target)) {
        setOpenRoleMenuId(null);
      }
    };
    if (openRoleMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openRoleMenuId]);

  // -- Render -------------------------------------------------------------
  if (!canViewUserRoles) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          You do not have access to User Role Management.
        </div>
      </div>
    );
  }

  const handleCopyPermissionsFromSource = () => {
    const sourceAccess = screenAccess[fetchFromRoleId] || {};
    setScreenAccess((prev) => ({
      ...prev,
      [wizardRoleId]: { ...sourceAccess },
    }));
    toast.success("Permissions copied successfully!");
  };

  const handleScreenAccessSaveAndNext = async () => {
    if (!canEditScreenAccess) {
      toast.error("You do not have permission to edit screen access.");
      return;
    }
    if (wizardRoleId) setSelectedRoleForAccess(wizardRoleId);
    setWizardStep(4);
  };

  const handleFinishWizard = async () => {
    if (!canEditDataAccess) {
      toast.error("You do not have permission to edit data access.");
      return;
    }
    try {
      const isDraftRole = !!pendingRoleDraft && String(wizardRoleId || "").startsWith("draft_");
      let finalRoleId = wizardRoleId;

      if (isDraftRole) {
        try {
          const response = await api.post("/global-settings/roles", pendingRoleDraft);
          finalRoleId = response?.data?.id || response?.data?.data?.id || Date.now().toString();
        } catch (apiError) {
          const stored = localStorage.getItem("roles");
          const currentList = stored ? JSON.parse(stored) : [];
          finalRoleId = Date.now().toString();
          currentList.push({
            id: finalRoleId,
            ...pendingRoleDraft,
            deletable: true,
            createdAt: new Date().toISOString(),
          });
          localStorage.setItem("roles", JSON.stringify(currentList));
        }
      }

      if (!finalRoleId) {
        toast.error("Role id missing. Please restart role creation.");
        return;
      }

      const effectiveScreenAccess =
        screenAccess[wizardRoleId] || screenAccess[finalRoleId] || {};
      const effectiveDataAccess =
        dataAccess[wizardRoleId] || dataAccess[finalRoleId] || {};

      try {
        await api.post("/global-settings/screen-access", {
          roleId: finalRoleId,
          screenAccess: sanitizeScreenAccessForSubmit(effectiveScreenAccess),
        });
      } catch {
        const stored = localStorage.getItem("screenAccess");
        const currentData = stored ? JSON.parse(stored) : {};
        currentData[finalRoleId] = sanitizeScreenAccessForSubmit(effectiveScreenAccess);
        localStorage.setItem("screenAccess", JSON.stringify(currentData));
      }

      try {
        await api.post("/global-settings/data-access", {
          roleId: finalRoleId,
          dataAccess: effectiveDataAccess,
        });
      } catch {
        const stored = localStorage.getItem("dataAccess");
        const currentData = stored ? JSON.parse(stored) : {};
        currentData[finalRoleId] = effectiveDataAccess;
        localStorage.setItem("dataAccess", JSON.stringify(currentData));
      }

      if (wizardRoleId && wizardRoleId !== finalRoleId) {
        setScreenAccess((prev) => {
          const next = { ...prev };
          if (next[wizardRoleId] && !next[finalRoleId]) {
            next[finalRoleId] = next[wizardRoleId];
          }
          delete next[wizardRoleId];
          return next;
        });
        setDataAccess((prev) => {
          const next = { ...prev };
          if (next[wizardRoleId] && !next[finalRoleId]) {
            next[finalRoleId] = next[wizardRoleId];
          }
          delete next[wizardRoleId];
          return next;
        });
      }

      setSelectedRoleForAccess(finalRoleId);
      toast.success(editingRole && !isDraftRole ? "Role updated successfully!" : "Role created successfully!");
      resetWizard();
      resetRoleForm();
      await fetchRoles();
    } catch (error) {
      console.error("Error completing role wizard:", error);
      toast.error("Failed to complete role creation. Please try again.");
    }
  };

  const handleExitRoleWizard = () => {
    resetWizard();
    resetRoleForm();
    setShowRoleForm(false);
  };

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {wizardStep === 1 && (
          <RoleListView
            roles={roles}
            searchRoles={searchRoles}
            setSearchRoles={setSearchRoles}
            showSystemRoles={showSystemRoles}
            setShowSystemRoles={setShowSystemRoles}
            openRoleMenuId={openRoleMenuId}
            setOpenRoleMenuId={setOpenRoleMenuId}
            roleMenuRef={roleMenuRef}
            onAddRole={() => {
              if (!canCreateRole) {
                toast.error("You do not have permission to create roles.");
                return;
              }
              resetRoleForm();
              setShowRoleForm(true);
            }}
            onEditRole={handleEditRole}
            onDeactivateRole={handleDeactivateRole}
            onReactivateRole={handleReactivateRole}
            onDeleteRole={handleDeleteRole}
            canCreateRole={canCreateRole}
            canEditRole={canEditRole}
            canDeleteRole={canDeleteRole}
          />
        )}
      </div>

      {wizardStep >= 2 &&
        createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-3 sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="role-wizard-title"
          >
            <div
              className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex shrink-0 flex-col border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <h2
                    id="role-wizard-title"
                    className="truncate text-base font-bold text-gray-900"
                  >
                    {editingRole
                      ? `Edit role${editingRole.name ? `: ${editingRole.name}` : ""}`
                      : "Create role"}
                  </h2>
                  <button
                    type="button"
                    onClick={handleExitRoleWizard}
                    className="shrink-0 rounded p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/40"
                    aria-label="Close role wizard"
                  >
                    <X className="h-5 w-5" aria-hidden />
                  </button>
                </div>
                <WizardStepsIndicator wizardStep={wizardStep} />
              </div>

              <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                {wizardStep === 2 && (
                  <FetchAccessView
                    roles={roles}
                    wizardRoleId={wizardRoleId}
                    shouldFetchAccess={shouldFetchAccess}
                    setShouldFetchAccess={setShouldFetchAccess}
                    fetchFromRoleId={fetchFromRoleId}
                    setFetchFromRoleId={setFetchFromRoleId}
                    onCopyPermissions={handleCopyPermissionsFromSource}
                    onPrevious={() => setWizardStep(1)}
                    onNext={() => {
                      if (wizardRoleId) setSelectedRoleForAccess(wizardRoleId);
                      setWizardStep(3);
                    }}
                  />
                )}

        {wizardStep === 3 && (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div
              className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-b border-orange-300/60 px-4 py-3"
              style={{
                background: "linear-gradient(90deg, #f96332 0%, #f4b977 100%)",
              }}
            >
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold uppercase tracking-wide text-white">
                  Screen Access Configuration
                </h3>
                <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                  Step 3
                </span>
              </div>
              <p className="text-xs font-medium text-white/90">
                Configure module and screen level permissions
              </p>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <ScreenAccessView
                catalog={accessCatalog}
                selectedRoleForAccess={selectedRoleForAccess}
                wizardRoleId={wizardRoleId}
                screenAccess={screenAccess}
                selectedModuleForAccess={selectedModuleForAccess}
                setSelectedModuleForAccess={setSelectedModuleForAccess}
                onScreenPermissionChange={handleScreenPermissionChange}
                onSelectAllModule={handleSelectAllModule}
                onPrevious={() => setWizardStep(editingRole ? 1 : 2)}
                onSaveAndNext={handleScreenAccessSaveAndNext}
                onCancel={() => {
                  resetWizard();
                  resetRoleForm();
                  setShowRoleForm(false);
                }}
                readOnly={!canEditScreenAccess}
              />
            </div>
          </div>
        )}

                {wizardStep === 4 && (
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold uppercase tracking-wide text-gray-800">
                          Data Access Configuration
                        </h3>
                        <span className="rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                          Step 4
                        </span>
                      </div>
                      <p className="text-xs font-medium text-gray-600">
                        Configure branch and department visibility
                      </p>
                    </div>
                    <div className="min-h-0 flex-1 overflow-hidden p-4">
                      <DataAccessView
                        selectedRoleForAccess={selectedRoleForAccess}
                        wizardRoleId={wizardRoleId}
                        dataAccess={dataAccess}
                        branches={branches}
                        departments={departments}
                        onDataAccessChange={handleDataAccessChange}
                        onDataAccessItemToggle={handleDataAccessItemToggle}
                        onSubmit={(e) => e.preventDefault()}
                        onCancel={() => {
                  resetWizard();
                  resetRoleForm();
                  setShowRoleForm(false);
                }}
                        showInlineActions={false}
                        readOnly={!canEditDataAccess}
                      />
                    </div>
                    <div className="flex flex-shrink-0 items-center justify-between border-t border-gray-300 bg-gray-200/80 px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setWizardStep(3)}
                        className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        onClick={handleFinishWizard}
                        disabled={!canEditDataAccess}
                        className="rounded-lg bg-redhalo-red px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/40"
                      >
                        Complete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

      <DeleteRoleConfirmModal
        open={deleteConfirmRoleId != null}
        roleName={
          roles.find((r) => String(r.id) === String(deleteConfirmRoleId))?.name
        }
        onConfirm={confirmDeleteRole}
        onCancel={cancelDeleteRole}
        loading={deleteRoleInProgress}
      />

      <RoleFormPopup
        showRoleForm={showRoleForm}
        editingRole={editingRole}
        formData={formData}
        handleInputChange={handleInputChange}
        setFormData={setFormData}
        wizardRoleType={wizardRoleType}
        setWizardRoleType={setWizardRoleType}
        roleAssignmentType={roleAssignmentType}
        setRoleAssignmentType={setRoleAssignmentType}
        filterResults={filterResults}
        setShowFilters={setShowFilters}
        onSubmit={handleRoleSubmit}
        onCancel={() => {
          resetRoleForm();
          resetWizard();
          setShowRoleForm(false);
        }}
      />
    </>
  );
}
